import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import type { CommentQuickViewId } from "@/lib/commentLabels";
import { displayAuthorName, platformLabel } from "@/lib/commentLabels";
import CommentsActiveFilters, {
  buildCommentFilterChips,
} from "@/components/comments/CommentsActiveFilters";
import CommentsFilterSidebar from "@/components/comments/CommentsFilterSidebar";
import CommentsList from "@/components/comments/CommentsList";
import CommentsPageShell from "@/components/comments/CommentsPageShell";
import CommentsQuickViews from "@/components/comments/CommentsQuickViews";
import VoiceResearchChat from "@/components/voice/VoiceResearchChat";
import { Button } from "@/components/ui/button";

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function deriveQuickView(
  action?: boolean,
  storyClass?: string,
  cta?: boolean,
  q?: string,
  month?: string,
  pain?: string,
): CommentQuickViewId {
  if (q?.trim() || month || pain) return "all";
  if (action) return "action";
  if (cta) return "cta";
  if (storyClass === "historia_identyfikacja") return "historia";
  if (storyClass === "pytanie") return "pytanie";
  if (storyClass === "atak_forma") return "atak";
  return "all";
}

function exportMarkdown(items: { author: string; text: string; platform: string; video_title: string; created_at: string }[]) {
  const lines = [
    "# Komentarze (eksport)",
    "",
    ...items.map((c, i) =>
      [
        `## ${i + 1}. ${displayAuthorName(c.author, c.platform)} · ${platformLabel(c.platform)}`,
        c.video_title,
        c.created_at,
        "",
        c.text,
        "",
      ].join("\n"),
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `komentarze-${new Date().toISOString().slice(0, 10)}.md`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Komentarze() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || params.get("video") || "");
  const [platform, setPlatform] = useState<string | undefined>(params.get("platform") || undefined);
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [storyClass, setStoryClass] = useState<string | undefined>(undefined);
  const [pain, setPain] = useState<string | undefined>(params.get("pain") || undefined);
  const [cta, setCta] = useState<boolean | undefined>(undefined);
  const [action, setAction] = useState<boolean | undefined>(undefined);
  const [hideReplies, setHideReplies] = useState(true);
  const [postId, setPostId] = useState<string | undefined>(params.get("post") || undefined);
  const [window, setWindow] = useState<string | undefined>("30d");
  const [searchMode, setSearchMode] = useState<"keyword" | "semantic">(
    params.get("mode") === "semantic" ? "semantic" : "keyword",
  );

  const debouncedQ = useDebounced(q, 300);

  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ) next.set("q", debouncedQ);
    if (postId) next.set("post", postId);
    if (pain) next.set("pain", pain);
    if (platform) next.set("platform", platform);
    if (searchMode === "semantic") next.set("mode", "semantic");
    setParams(next, { replace: true });
  }, [debouncedQ, postId, pain, platform, searchMode, setParams]);

  const filters = {
    q: debouncedQ || undefined,
    platform,
    window,
    month,
    class: storyClass,
    pain,
    cta,
    is_reply: hideReplies ? false : undefined,
    post_id: postId,
    action,
    mode: searchMode,
  };

  const { data: facetsData } = useQuery({
    queryKey: ["comments-facets"],
    queryFn: api.commentsFacets,
  });
  const { data: listData, isLoading, isFetching } = useQuery({
    queryKey: ["comments-list", filters],
    queryFn: () => api.listComments({ ...filters, limit: 80 }),
  });
  const { data: postsData } = useQuery({
    queryKey: ["comments-posts", window, platform],
    queryFn: () => api.commentsPosts({ limit: 12, window, platform }),
  });

  const facets = facetsData?.payload;
  const posts = postsData?.payload;

  const searchHint = (() => {
    if (searchMode !== "semantic") return null;
    if (listData?.search_fallback === "keyword") {
      return "Indeks niedostępny: użyto wyszukiwania po słowach.";
    }
    const at = listData?.indexed_at;
    const n = listData?.indexed_count ?? 0;
    if (!at) return "Brak indeksu kontekstowego. Odśwież snapshot na Statystykach.";
    const day = String(at).slice(0, 10);
    return `Indeks: ${day} · ${n} wektorów`;
  })();

  const quickView = deriveQuickView(action, storyClass, cta, debouncedQ, month, pain);
  const quickCounts = useMemo(
    () => ({
      all: facets?.total,
      action: facets?.action,
      historia: facets?.historia,
      cta: facets?.cta,
      pytanie: facets?.pytanie,
      atak: facets?.atak_forma,
    }),
    [facets],
  );

  const clearFilters = () => {
    setQ("");
    setPlatform(undefined);
    setMonth(undefined);
    setStoryClass(undefined);
    setPain(undefined);
    setCta(undefined);
    setAction(undefined);
    setPostId(undefined);
    setWindow("30d");
    setHideReplies(true);
  };

  const applyQuick = (id: CommentQuickViewId) => {
    setQ("");
    setMonth(undefined);
    setPain(undefined);
    setPostId(undefined);
    setStoryClass(undefined);
    setCta(undefined);
    setAction(undefined);
    if (id === "action") setAction(true);
    if (id === "historia") setStoryClass("historia_identyfikacja");
    if (id === "cta") setCta(true);
    if (id === "pytanie") setStoryClass("pytanie");
    if (id === "atak") setStoryClass("atak_forma");
  };

  const chips = buildCommentFilterChips(
    { q, platform, storyClass, pain, month, postId, window, action, cta, hideReplies },
    {
      clearQ: () => setQ(""),
      clearPlatform: () => setPlatform(undefined),
      clearClass: () => setStoryClass(undefined),
      clearPain: () => setPain(undefined),
      clearMonth: () => setMonth(undefined),
      clearPost: () => setPostId(undefined),
      clearWindow: () => setWindow("30d"),
      clearAction: () => setAction(undefined),
      clearCta: () => setCta(undefined),
      showReplies: () => setHideReplies(true),
    },
  );

  return (
    <CommentsPageShell
      tab="browse"
      showSearch
      q={q}
      onQChange={setQ}
      searchMode={searchMode}
      onSearchModeChange={setSearchMode}
      searchHint={searchHint}
      searchPending={isFetching}
      indexedCount={listData?.indexed_count ?? null}
      total={listData?.total ?? facets?.total ?? 0}
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={() => exportMarkdown(listData?.items || [])}
          disabled={!listData?.items?.length}
        >
          <Download className="mr-2 h-3.5 w-3.5" />
          Eksport MD
        </Button>
      }
      aside={<VoiceResearchChat surface="comments_chat" />}
    >
      <CommentsQuickViews activeId={quickView} counts={quickCounts} onSelect={applyQuick} />

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <CommentsFilterSidebar
          platforms={facets?.platforms || {}}
          classes={facets?.classes || {}}
          months={facets?.months || {}}
          pains={facets?.pains || {}}
          platform={platform}
          storyClass={storyClass}
          month={month}
          pain={pain}
          hideReplies={hideReplies}
          window={window}
          onPlatform={setPlatform}
          onClass={setStoryClass}
          onMonth={setMonth}
          onPain={setPain}
          onHideReplies={setHideReplies}
          onWindow={setWindow}
        />

        <div className="min-w-0 flex-1">
          <CommentsActiveFilters chips={chips} onClearAll={clearFilters} />
          {(posts?.items || []).length > 0 && (
            <div className="mb-4 rounded-xl border border-slate-200 bg-white p-3">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Top rolki</h2>
              <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                {(posts?.items || []).slice(0, 8).map((p) => (
                  <button
                    key={`${p.platform}-${p.post_id}`}
                    type="button"
                    className="min-w-[140px] max-w-[180px] shrink-0 rounded-lg bg-slate-50 px-2 py-1.5 text-left hover:bg-slate-100"
                    onClick={() => setPostId(p.post_id)}
                  >
                    <div className="truncate text-xs font-medium text-slate-800">{p.title || p.post_id}</div>
                    <div className="text-[10px] text-slate-500">
                      {platformLabel(p.platform)} · {p.comments}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <CommentsList items={listData?.items ?? []} isLoading={isLoading} total={listData?.total ?? 0} />
        </div>
      </div>
    </CommentsPageShell>
  );
}
